/**
 * The way in: search, start something new, and the documents you already have.
 *
 * LAID OUT AS GOOGLE DOCS IS, because the shape is familiar and the shape is right: one search
 * across everything, a band of ways to start, then the documents themselves. What it is NOT is a
 * copy of the semantics -- a controlled document has a status and a lifecycle, so the cards carry
 * those where Google carries "Opened Oct 1".
 *
 * THE LIST IS QMS's. Which documents exist, and which of them this persona may see, are t1's
 * questions; t11 only adds what is written inside them. Changing persona changes the answer, so the
 * page listens for that and refetches rather than reading storage once.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert, Box, CircularProgress, IconButton, InputAdornment, Menu, MenuItem,
    Paper, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import {
    Search as SearchIcon, Close, GridViewOutlined, ViewListOutlined,
    MoreVert, AddOutlined,
} from "@mui/icons-material";

import useAppContext from "../util/AppContext";
import { makeCresAxios } from "../util/CresAxios";
import EventBus from "../util/EventBus";
import Splash from "./Splash";
import DocumentCard, { DocumentRow } from "../components/Docs/DocumentCard";
import DocumentTable from "../components/Docs/DocumentTable";
import DocumentFilterBar from "../components/Docs/DocumentFilterBar";
import VisibilityModeTabs from "../components/Docs/VisibilityModeTabs";
import {
    DocumentFilters, NO_FILTERS, SORT_OPTIONS, SortKey,
    applyFilters, applySort, statisticsOf,
} from "../util/documentFilters";
import { byLabel, merged, plain } from "../util/documentSearch";
import { Grey5, Grey17 } from "../theme/styles";

/** How long to wait after the last keystroke before searching. */
const SEARCH_DEBOUNCE_MS = 350;

/**
 * How many documents to fetch.
 *
 * ONE FETCH, AND EVERYTHING ON THE PAGE DEPENDS ON IT. The grid, the list, the filters and the
 * counts beside them are all computed from this one array, which is what lets the two views agree
 * and the counts match what is on screen. So this number is not a page size -- it is how much of
 * the tenant this screen can be right about.
 *
 * IT WAS 200, AND PEAK HAS 830 under an Organization persona. The page read "showing 180 of 200"
 * and the 200 was the fetch, not the tenant: documents 201 onwards were absent from the grid, from
 * the list, and from every count in the Filter By band, with nothing on screen saying so.
 *
 * IT IS THIS HIGH ONLY BECAUSE THE ROW GOT SMALLER. Get Documents now returns the thirteen fields
 * this screen reads instead of the whole ObjectRevision -- 454 bytes a row, measured, against 6,858
 * before, 61% of which was a base64 form schema no part of this page has ever looked at. Peak's 830
 * documents went from 5.7MB to 377KB, and 5.7MB was already at Lambda's 6MB response ceiling, which
 * is reached as a 502 rather than as a slow page.
 *
 * SO 2000 IS NOT THE PAYLOAD LIMIT. At 454 bytes a row the 6MB ceiling is somewhere past 13,000;
 * what binds first is this page drawing a card per document with no virtualisation, which 830
 * already makes you wait for. 2000 is where those two meet, and it is a number to revisit by
 * measuring rather than by raising.
 *
 * IT IS STILL A CAP, and the honest version is worth writing down: a tenant with more document
 * REVISIONS than this -- every revision is a row here, not every document -- needs server-side
 * paging for the list, the filters AND the counts together. Not a bigger number, which only moves
 * the point at which the page starts quietly lying about how much it is showing.
 */
const FETCH_LIMIT = 2000;

type View = "grid" | "list";

export default function Documents() {

    const sessionContext = useAppContext()?.sessionContext as any;
    const navigate = useNavigate();

    const [rows, setRows] = useState<DocumentRow[]>([]);
    const [results, setResults] = useState<any[] | null>(null);
    const [query, setQuery] = useState("");
    const [view, setView] = useState<View>(
        // Remembered per browser, because it is a preference about reading rather than about data.
        (() => { try { return (localStorage.getItem("docs.view") as View) || "grid"; } catch { return "grid"; } })());
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [tableMenu, setTableMenu] = useState<HTMLElement | null>(null);

    const [filters, setFilters] = useState<DocumentFilters>(NO_FILTERS);
    // The band starts SHUT. It is a page of documents first; somebody who wants to narrow them
    // opens it, and it stays open for the session once they have.
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [sort, setSort] = useState<{ key: SortKey; descending: boolean }>(
        // Remembered per browser alongside the view, because both are preferences about reading.
        (() => {
            try {
                const stored = JSON.parse(localStorage.getItem("docs.sort") || "null");
                if (stored && SORT_OPTIONS.some(o => o.key === stored.key)) return stored;
            } catch { /* private window */ }
            return { key: "DRAFTS" as SortKey, descending: false };
        })());

    /**
     * Is there a session at all?
     *
     * SIGNED OUT, THIS PAGE SHOWS NOTHING, and that is a decision rather than an omission. Every
     * part of it is about THIS PERSON'S documents -- the list, the search over them, the persona
     * they are reading as, the band offering to start one. With no session each of those is either
     * a 401 dressed as an error message or a control that cannot do anything, and a wall of them is
     * a product that looks broken rather than one waiting to be signed into. The NavBar carries
     * the way in.
     */
    const signedIn = !!sessionContext?.valid?.();

    const axios = useMemo(() => makeCresAxios(sessionContext), [sessionContext]);

    const persona = useCallback(() => {
        const v = sessionContext?.getActiveVisibility?.() || {};
        return { "visibility-mode": v.Mode || v.mode, "visibility-value": v.Value || v.value };
    }, [sessionContext]);

    /**
     * The address of a picture of page one, for a card that wants one.
     *
     * CACHED FOR THE LIFE OF THE PAGE. Cards unmount and remount as the grid filters and sorts, and
     * without this each remount would ask again -- a render and a store on the server for a picture
     * the browser already had.
     *
     * THE PROMISE IS WHAT IS CACHED, not the answer. Twenty cards mount in the same tick; caching
     * the result would let all twenty ask before the first came back.
     */
    const pictures = React.useRef(new Map<string, Promise<string | null>>());
    const thumbnailFor = useCallback((partUSID: string) => {
        const already = pictures.current.get(partUSID);
        if (already) return already;
        const asked = axios.get(`/t11/v1/document_thumbnail?USID=${encodeURIComponent(partUSID)}`,
                                { headers: persona() })
            .then((response: any) => response?.data?.Url ?? null)
            // A CARD WITHOUT A PICTURE IS A CARD WITH ITS WORDS. Refused, missing or simply slow,
            // the text miniature is what the reader gets and nothing on the page says sorry.
            .catch(() => null);
        pictures.current.set(partUSID, asked);
        return asked;
    }, [axios, persona]);

    const load = useCallback(async () => {
        // NOT EVEN ASKED WHEN THERE IS NO SESSION. The 401 is correct and the retry on it is
        // correct; what is pointless is making the request at all, and the console full of
        // refusals is the first thing somebody debugging this page would chase.
        if (!signedIn) { setLoading(false); return; }
        setLoading(true);
        try {
            const response = await axios.get(`/t11/v1/documents?Limit=${FETCH_LIMIT}&Offset=0`,
                                             { headers: persona() });
            const list = response?.data?.PlatformMessage?.ListOfObjectRevision?.ObjectRevision ?? [];
            setRows(Array.isArray(list) ? list : [list]);
            setProblem(null);
        } catch (error: any) {
            setProblem(error?.response?.data?.Cause || error?.response?.data?.Code
                       || "The document list could not be loaded.");
        } finally {
            setLoading(false);
        }
    }, [axios, persona, signedIn]);

    useEffect(() => { load(); }, [load]);

    // REFETCH ON PERSONA CHANGE. The menu emits this; the list is a different list afterwards.
    useEffect(() => {
        const onChange = () => load();
        EventBus.visibility.addListener("change-visibility", onChange);
        return () => { EventBus.visibility.removeListener("change-visibility", onChange); };
    }, [load]);

    // ---------------------------------------------------------------- search

    useEffect(() => {
        const text = query.trim();
        if (!text) { setResults(null); setSearching(false); return; }
        setSearching(true);
        const timer = window.setTimeout(async () => {
            try {
                const response = await axios.get(
                    `/t11/v1/document_search?Query=${encodeURIComponent(text)}&Limit=100`,
                    { headers: persona() });
                setResults(response?.data?.Results ?? []);
            } catch {
                setResults([]);
            } finally {
                setSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [query, axios, persona]);

    // ---------------------------------------------------------------- actions

    const perform = useCallback(async (revisionUSID: string, action: string) => {
        setBusy(action);
        try {
            await axios.put("/t11/v1/document_lifecycle",
                            { RevisionUSID: revisionUSID, Action: action },
                            { headers: persona() });
            await load();
        } catch (error: any) {
            // THE SERVER'S OWN SENTENCE. Release, Revise and the rest are refused by t1 with
            // reasons written for a person -- "Only a DRAFT revision may be released" -- and a
            // message invented here would be a worse version of one we already have.
            setProblem(error?.response?.data?.Cause || error?.response?.data?.Code
                       || "That action could not be completed.");
        } finally {
            setBusy(null);
        }
    }, [axios, load, persona]);

    const chooseView = (next: View | null) => {
        if (!next) return;
        setView(next);
        try { localStorage.setItem("docs.view", next); } catch { /* private window */ }
    };

    // Search results are the same documents, so they render through the same components -- and
    // through the same rows, so the grid and the list agree about what is being shown.
    const found: DocumentRow[] = useMemo(() => {
        // TYPED, BUT THE BODY SEARCH HAS NOT COME BACK YET. Showing every document for those few
        // hundred milliseconds reads as the search having done nothing; the name matches are
        // already here and are narrowing in the right direction.
        if (!results) return query.trim() ? byLabel(rows, query) : rows;

        const bodies: DocumentRow[] = results.map(r => ({
            USID: r.RevisionUSID, ObjectID: r.ObjectID, Name: r.Name,
            Status: r.Status, DisplayRevision: r.DisplayRevision,
            ObjectTypeAcronym: r.ObjectTypeAcronym,
            ObjectTypeName: r.ObjectTypeName,
            CreatedDatetime: r.CreatedDatetime,
            DocumentCount: 1,
            LastModifiedDatetime: r.LastModifiedDatetime,
            // The markers stay on Snippet, which is what draws the match; Preview is the same
            // sentence as prose, for the thumbnail that is not showing a search.
            Snippet: r.Snippet ?? "",
            Preview: plain(r.Snippet), HasBody: true,
        }));

        // AND THE DOCUMENTS WHOSE NAME OR NUMBER WAS TYPED -- from `rows`, which is this persona's
        // own list, so nothing here can surface a document the reader was not already shown. The
        // index itself stays body-only; see util/documentSearch.
        return merged(byLabel(rows, query), bodies);
    }, [results, rows, query]);

    // COUNTED BEFORE FILTERING, so ticking Draft does not reduce every other count to zero and
    // leave the band looking broken -- the numbers say what ticking a box WOULD give you.
    const statistics = useMemo(() => statisticsOf(found), [found]);

    // MEMOISED, AND THE TABLE IS WHY. The list view feeds this array into its enterprise component
    // and invalidates it when the array changes; rebuilding it on every render made every render a
    // new array, so the table invalidated, re-queried, set state, re-rendered, and went round
    // again -- an infinite loop in list view only, which grid view never shows.
    const shown: DocumentRow[] = useMemo(
        () => applySort(applyFilters(found, filters), sort.key, sort.descending),
        [found, filters, sort]);

    /**
     * The table chose an order; the cards are drawn in it too.
     *
     * THE TABLE REMEMBERS ITS OWN CHOICE, so this does not write to storage -- two remembered sorts
     * would disagree the moment either was cleared, and the table's is the one with the UI attached.
     */
    const followTableSort = useCallback((next: { key: SortKey; descending: boolean } | null) => {
        // SET ONLY ON A REAL CHANGE. setSort with a fresh object of equal value is still a state
        // change to React, and this page feeds the table the array it re-sorts -- so an equal-value
        // set is a render loop, not a wasted render. The component guards its side too; both,
        // because either one alone is a guard somebody can delete without the tests noticing.
        if (!next) return;
        setSort(current => (current.key === next.key && current.descending === next.descending)
                            ? current : next);
    }, []);

    /** Is the page showing a search, whichever half of it has answered. */
    const searchActive = !!query.trim();

    // THE SPLASH, rather than a page of controls that cannot act. See `signedIn` above. It used to
    // be `null` -- defensible for a list of a tenant's documents, and it meant the app's own
    // address answered a visitor with a header above an empty page and no visible way in.
    if (!signedIn) return <Splash />;

    return (
        <Box sx={{ pb: 6 }}>
            {/* ---------------- search ---------------- */}
            <Box sx={{ display: "flex", justifyContent: "center", pt: 3, pb: 3 }}>
                <TextField
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search the text of every document"
                    size="small"
                    sx={{ width: "min(720px, 92%)",
                          "& .MuiOutlinedInput-root": { borderRadius: 999, backgroundColor: "#F1F3F4" },
                          "& fieldset": { border: "none" } }}
                    slotProps={{ input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                {searching ? <CircularProgress size={18}/> : <SearchIcon sx={{ color: Grey5 }}/>}
                            </InputAdornment>),
                        endAdornment: query ? (
                            <InputAdornment position="end">
                                <IconButton size="small" aria-label="Clear search" onClick={() => setQuery("")}>
                                    <Close sx={{ fontSize: 18 }}/>
                                </IconButton>
                            </InputAdornment>) : undefined,
                    }}}
                />
            </Box>

            {/* ---------------- start something new ---------------- */}
            {!searchActive && (
                <Box sx={{ backgroundColor: "#F8F9FA", py: 2.5, px: 4,
                           borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider" }}>
                    <Box sx={{ maxWidth: 1180, mx: "auto" }}>
                        <Typography sx={{ fontSize: 15, color: Grey17, mb: 1.5 }}>Start a new document</Typography>
                        <Paper variant="outlined"
                               sx={{ width: 150, height: 195, borderRadius: 2, display: "flex",
                                     alignItems: "center", justifyContent: "center", cursor: "pointer",
                                     "&:hover": { borderColor: "primary.main" } }}
                               onClick={() => navigate("/document/new")}>
                            <AddOutlined sx={{ fontSize: 44, color: "#C9CBD2" }}/>
                        </Paper>
                        <Typography sx={{ fontSize: 13, color: Grey17, mt: 1 }}>Blank document</Typography>
                    </Box>
                </Box>
            )}

            {/* ---------------- who you are reading as ---------------- */}
            {/* ABOVE THE FILTERS, because the two do different things and the order says so: the
                persona decides which documents EXIST for this caller, and the band below narrows
                the list that comes back. Widening to Organization is not "show me more rows". */}
            <VisibilityModeTabs/>

            {/* ---------------- filter by ---------------- */}
            {/* ABOVE THE DOCUMENTS AND BELOW THE WAYS TO START ONE, so the page reads top to
                bottom: find, start, narrow, browse. It is offered during a search too -- a search
                over the prose of 23 documents is exactly where narrowing to the drafts helps. */}
            <DocumentFilterBar filters={filters} statistics={statistics}
                               open={filtersOpen} onOpenChange={setFiltersOpen}
                               onChange={setFilters} onClear={() => setFilters(NO_FILTERS)}/>

            {/* ---------------- the documents ---------------- */}
            {/* WIDER FOR THE LIST. The cards wrap to whatever room they are given; the table does
                not -- its columns add up to a fixed width and anything past the container's edge is
                simply cut off, taking the row menu with it. */}
            <Box sx={{ maxWidth: view === "list" ? 1280 : 1180, mx: "auto", px: 4, pt: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    {/* IN LIST VIEW THE TABLE DRAWS ITS OWN HEADING AND ITS OWN REFRESH, so this row
                        carries only the view toggle. Drawing them here as well put two ⋮ menus and
                        two titles within an inch of each other, both saying Refresh and only one of
                        them belonging to the table underneath. */}
                    <Typography sx={{ fontSize: 16, color: Grey17 }}>
                        {searchActive ? `${shown.length} matching document${shown.length === 1 ? "" : "s"}`
                                 : view === "list" ? "" : "Documents"}
                    </Typography>
                    {/* HOW MANY OF HOW MANY, whenever the two differ. The table carries its own
                        count and the cards carry none, so without this a filtered grid is simply a
                        shorter page -- which reads as a tenant with fewer documents in it. */}
                    {shown.length !== found.length && (
                        <Typography sx={{ fontSize: 12, color: Grey5, ml: 1 }}>
                            showing {shown.length} of {found.length}
                        </Typography>
                    )}
                    <Box sx={{ flexGrow: 1 }}/>

                    {/* NO SORT CONTROL HERE. It is the table's -- its menu, its remembered
                        choice, its direction on the column header, the same as every other list in
                        the suite. The grid has no control of its own and does not need a second
                        one: it is drawn from the list the table has ordered, so choosing an order
                        in list view is the order the cards are in when you switch back. */}

                    <ToggleButtonGroup exclusive size="small" value={view}
                                       onChange={(_, v) => chooseView(v)}>
                        <ToggleButton value="list" aria-label="List view"><ViewListOutlined sx={{ fontSize: 19 }}/></ToggleButton>
                        <ToggleButton value="grid" aria-label="Grid view"><GridViewOutlined sx={{ fontSize: 19 }}/></ToggleButton>
                    </ToggleButtonGroup>
                    {/* t1's list menu is a single option and this one matches it deliberately. */}
                    {view === "grid" && (
                        <>
                            <Tooltip title="More">
                                <IconButton size="small" aria-label="List actions" sx={{ ml: 1 }}
                                            onClick={(e) => setTableMenu(e.currentTarget)}>
                                    <MoreVert sx={{ fontSize: 19 }}/>
                                </IconButton>
                            </Tooltip>
                            <Menu anchorEl={tableMenu} open={!!tableMenu} onClose={() => setTableMenu(null)}>
                                <MenuItem onClick={() => { setTableMenu(null); load(); }}>Refresh</MenuItem>
                            </Menu>
                        </>
                    )}
                </Box>

                {problem && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setProblem(null)}>{problem}</Alert>}

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress/></Box>
                ) : shown.length === 0 ? (
                    <Alert severity="info">
                        {/* THREE DIFFERENT NOTHINGS, and telling them apart is the whole value of
                            the message: a filter you forgot you set, a search that found nothing,
                            and a persona that covers no documents each need a different next
                            move. The filtered case is checked first because it is the one the
                            reader can undo from here. */}
                        {found.length > 0 ? "No documents match the filters above."
                                          : searchActive ? "Nothing matched that search."
                                                    : "No documents are visible under your current persona."}
                    </Alert>
                ) : view === "grid" ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {shown.map(row => (
                            <DocumentCard key={row.USID} row={row} busy={busy}
                                          onOpen={() => navigate(`/document/${row.USID}`)}
                                          onAction={(a) => perform(row.USID, a)}
                                          thumbnailFor={thumbnailFor}/>
                        ))}
                    </Box>
                ) : (
                    <DocumentTable rows={shown} busy={busy}
                                   onOpen={(usid) => navigate(`/document/${usid}`)}
                                   onAction={perform} onRefresh={load}
                                   onSort={followTableSort}/>
                )}
            </Box>
        </Box>
    );
}
